import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/trpc/react';
import type { SubscriptionPlan } from '@unriddle/db/types';
import {
  getDailyPageLimit,
  getFastImportLimit,
  getHighResImportLimit,
  getImportLimit,
  getRecordingLimit,
  getWordLimit,
} from '@unriddle/validators/limits';
import { getUserPlanDetails } from '@unriddle/validators/plan';
import { useMemo } from 'react';

const useLimits = () => {
  const { user, isLoggedIn } = useAuth();

  const utils = api.useUtils();
  const { data } = api.user.limits.useQuery(undefined, {
    enabled: isLoggedIn,
  });

  const limits = useMemo(() => {
    if (!user) return null;

    const planName = getUserPlanDetails(
      user,
    ).planName.toUpperCase() as SubscriptionPlan;

    const fastImportLimit = getFastImportLimit(planName);
    const highResImportLimit = getHighResImportLimit(planName);
    const legacyImportLimit = getImportLimit(planName);
    const dailyPageLimit = getDailyPageLimit(planName);
    const wordLimit = getWordLimit(planName);
    const recordingLimit = getRecordingLimit(planName);

    return {
      fastImportLimit,
      highResImportLimit,
      legacyImportLimit,
      dailyPageLimit,
      wordLimit,
      recordingLimit,
    };
  }, [user]);

  const exceededLimits = useMemo(() => {
    if (!limits || !data) return null;

    const isImportLimitExceeded =
      limits.legacyImportLimit &&
      data?.legacyImportCount >= limits.legacyImportLimit;

    const isDailyPageLimitExceeded =
      limits.dailyPageLimit && data?.dailyPageCount >= limits.dailyPageLimit;

    const isWordLimitExceeded =
      limits.wordLimit && data?.wordCount >= limits.wordLimit;
    const isRecordingLimitExceeded =
      limits.recordingLimit && data?.recordingCount >= limits.recordingLimit;

    return {
      isImportLimitExceeded,
      isDailyPageLimitExceeded,
      isWordLimitExceeded,
      isRecordingLimitExceeded,
    };
  }, [limits, data]);

  const optimisticIncreaseImportLimit = () => {
    utils.user.limits.setData(undefined, (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        legacyImportCount: prev.legacyImportCount + 1,
      };
    });
  };

  const optimisticIncreaseRecordingLimit = () => {
    utils.user.limits.setData(undefined, (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        recordingCount: prev.recordingCount + 1,
      };
    });
  };

  return {
    exceededLimits,
    limits,
    usage: data,
    optimisticIncreaseImportLimit,
    optimisticIncreaseRecordingLimit,
  };
};

export default useLimits;
